import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Alert from "../common/Alert";
import {
  useLazyGetPosPrintJobQuery,
  usePrintPosInvoiceReceiptMutation,
} from "../../store/api";
import {
  getPosPrintErrorMessage,
  getPosPrintStatusMeta,
  getPrintJobFailureMessage,
  isEmptyPrintJobId,
  POS_PRINT_STATUSES,
} from "../../utils/posPrintHandler";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 45000;

const PrintReceiptAction = ({
  invoiceId,
  invoiceNo,
  buttonLabel = "Print Receipt",
  variant = "secondary",
  size = "sm",
  className = "",
  disabled = false,
  onPrinted,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [printJobId, setPrintJobId] = useState(null);
  const [localError, setLocalError] = useState("");
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const [triggerPrint, { isLoading: isSubmitting }] =
    usePrintPosInvoiceReceiptMutation();
  const [fetchPrintJob, { data: printJob, isFetching: isPolling }] =
    useLazyGetPosPrintJobQuery();

  const statusMeta = useMemo(
    () => getPosPrintStatusMeta(printJob?.status),
    [printJob?.status],
  );

  const resetPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => resetPolling, []);

  useEffect(() => {
    if (!modalOpen || !printJobId) return undefined;

    resetPolling();

    fetchPrintJob(printJobId, true);

    intervalRef.current = setInterval(() => {
      fetchPrintJob(printJobId, true);
    }, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      setPollTimedOut(true);
      resetPolling();
    }, POLL_TIMEOUT_MS);

    return resetPolling;
  }, [modalOpen, printJobId, fetchPrintJob]);

  useEffect(() => {
    const status = String(printJob?.status || "").toUpperCase();
    if (!status) return;

    if (
      status === POS_PRINT_STATUSES.PRINTED ||
      status === POS_PRINT_STATUSES.FAILED
    ) {
      resetPolling();
    }

    if (status === POS_PRINT_STATUSES.PRINTED && onPrinted) {
      onPrinted(printJob);
    }
  }, [printJob, onPrinted]);

  const handleClose = () => {
    resetPolling();
    setModalOpen(false);
    setPrintJobId(null);
    setPollTimedOut(false);
    setLocalError("");
  };

  const handleStartPrint = async () => {
    if (!invoiceId || isSubmitting || disabled) return;

    setLocalError("");
    setPrintJobId(null);
    setPollTimedOut(false);
    setModalOpen(true);

    try {
      const result = await triggerPrint(invoiceId).unwrap();
      const nextPrintJobId = result?.printJobId;

      if (isEmptyPrintJobId(nextPrintJobId)) {
        setLocalError("Printing is not configured for this store or device.");
        return;
      }

      setPrintJobId(nextPrintJobId);
    } catch (err) {
      setLocalError(getPosPrintErrorMessage(err));
    }
  };

  const isPrinted =
    String(printJob?.status || "").toUpperCase() === POS_PRINT_STATUSES.PRINTED;
  const isFailed =
    String(printJob?.status || "").toUpperCase() === POS_PRINT_STATUSES.FAILED;
  const effectiveError = pollTimedOut
    ? "Print status timed out. You can retry the receipt print request."
    : localError || (isFailed ? getPrintJobFailureMessage(printJob) : "");

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={handleStartPrint}
        disabled={disabled || isSubmitting || !invoiceId}
      >
        {isSubmitting ? "Starting..." : buttonLabel}
      </Button>

      <Modal
        isOpen={modalOpen}
        onClose={handleClose}
        title={invoiceNo ? `Print Invoice #${invoiceNo}` : "Print Receipt"}
        confirmText={isPrinted ? "Done" : effectiveError ? "Retry" : "Close"}
        showCancel={false}
        onConfirm={
          isPrinted ? handleClose : effectiveError ? handleStartPrint : handleClose
        }
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/50">
            <div className="flex items-center gap-3">
              {!effectiveError && !isPrinted && (
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
              )}
              {isPrinted && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
              {effectiveError && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {effectiveError ? "Print issue" : statusMeta.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {effectiveError || statusMeta.message}
                </p>
              </div>
            </div>
          </div>

          {effectiveError && <Alert tone="error">{effectiveError}</Alert>}

          <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm dark:border-slate-700 dark:bg-slate-900/50">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Invoice
              </div>
              <div className="mt-1 font-medium text-slate-900 dark:text-slate-100">
                {invoiceNo || "Selected invoice"}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Print Job
              </div>
              <div className="mt-1 font-medium text-slate-900 dark:text-slate-100">
                {printJobId || "Pending"}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Status
              </div>
              <div className="mt-1 font-medium text-slate-900 dark:text-slate-100">
                {printJob?.status || (isSubmitting ? "SUBMITTING" : "QUEUED")}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Attempts
              </div>
              <div className="mt-1 font-medium text-slate-900 dark:text-slate-100">
                {printJob?.attempts ?? (isPolling ? 1 : 0)}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PrintReceiptAction;
